import React, { useState, useCallback, useMemo, useEffect } from "react";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { StatusBadge } from "@/components/ui/StatusBadge";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  CheckCircle, XCircle, Clock, User, Shield, CreditCard,
  Calendar, DollarSign, TrendingUp, FileText, Loader,
  Check, AlertTriangle, Printer, Download, X, Phone,
  Mail, Building2, Wallet, Target, Users, ArrowUpRight,
  ArrowDownRight, ChevronRight, Info, Briefcase, Hash,
  Scale, BarChart3, History, MessageSquare, Send,
  CircleDot, GitBranch, Stamp, Eye, File,
} from "lucide-react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { useAuth } from "@/context/auth.context";
import { cn, formatCurrency } from "@/lib/utils";
import { format } from "date-fns";

// ─── Schemas ──────────────────────────────────────────────────────────────────
const approvalSchema = z.object({
  notes: z.string().optional(),
});

// ─── Types ────────────────────────────────────────────────────────────────────
interface LoanApprovalModalProps {
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
  loanId: number;
}

// ─── Utility ──────────────────────────────────────────────────────────────────
const formatMemberNumber = (code?: string | null) => {
  if (!code) return "N/A";
  const num = parseInt(String(code).replace(/\D/g, ""), 10);
  return isNaN(num) ? code : `#${String(num).padStart(4, "0")}`;
};

const getInitials = (name?: string | null) => {
  if (!name) return "?";
  return name.split(" ").filter(Boolean).map(w => w[0]).join("").toUpperCase().slice(0, 2);
};

// ─── Risk Level Badge ─────────────────────────────────────────────────────────
function RiskBadge({ level }: { level: string }) {
  const config: Record<string, { color: string; bg: string }> = {
    LOW: { color: "text-emerald-700", bg: "bg-emerald-50 border-emerald-200" },
    MEDIUM: { color: "text-amber-700", bg: "bg-amber-50 border-amber-200" },
    HIGH: { color: "text-red-700", bg: "bg-red-50 border-red-200" },
  };
  const c = config[level] || config.LOW;
  return (
    <span className={cn("inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-bold border", c.bg, c.color)}>
      {level}
    </span>
  );
}

// ─── Metric Card ──────────────────────────────────────────────────────────────
function MetricCard({ label, value, icon, color = "gray" }: {
  label: string; value: string | number; icon: React.ReactNode; color?: string;
}) {
  const colors: Record<string, string> = {
    green: "bg-emerald-50 text-emerald-600",
    blue: "bg-blue-50 text-blue-600",
    red: "bg-red-50 text-red-600",
    amber: "bg-amber-50 text-amber-600",
    purple: "bg-purple-50 text-purple-600",
    gray: "bg-gray-50 text-gray-600",
  };
  return (
    <div className="rounded-xl border border-gray-100 bg-white p-3.5">
      <div className="flex items-center justify-between mb-2">
        <span className="text-[11px] font-medium text-gray-500 uppercase tracking-wide">{label}</span>
        <div className={cn("flex h-7 w-7 items-center justify-center rounded-lg", colors[color] || colors.gray)}>
          {icon}
        </div>
      </div>
      <p className="text-lg font-bold text-gray-900">{value}</p>
    </div>
  );
}

// ─── Section Header ───────────────────────────────────────────────────────────
function SectionHeader({ icon, title }: { icon: React.ReactNode; title: string }) {
  return (
    <div className="flex items-center gap-2.5 mb-4">
      <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-[#009A44]/10">
        <div className="h-4 w-4 text-[#009A44]">{icon}</div>
      </div>
      <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wide">{title}</h3>
    </div>
  );
}

// ─── Skeleton Loader ──────────────────────────────────────────────────────────
function ModalSkeleton() {
  return (
    <div className="space-y-6 p-6">
      <div className="flex gap-4">
        <Skeleton className="h-20 w-full rounded-xl" />
        <Skeleton className="h-20 w-48 rounded-xl" />
      </div>
      <div className="grid grid-cols-3 gap-4">
        {[1, 2, 3, 4, 5, 6].map(i => <Skeleton key={i} className="h-24 rounded-xl" />)}
      </div>
      <Skeleton className="h-64 rounded-xl" />
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// MAIN MODAL COMPONENT
// ══════════════════════════════════════════════════════════════════════════════
export default function LoanApprovalModal({
  isOpen,
  setIsOpen,
  loanId,
}: LoanApprovalModalProps) {
  const [approvalAction, setApprovalAction] = useState<"approve" | "reject" | null>(null);
  const queryClient = useQueryClient();
  const { user } = useAuth();

  const form = useForm<z.infer<typeof approvalSchema>>({
    resolver: zodResolver(approvalSchema),
    defaultValues: { notes: "" },
  });

  // ─── Fetch Data ───
  const { data, isLoading, error } = useQuery({
    queryKey: ["loan-approval-status", loanId],
    queryFn: async () => {
      const { data } = await api.get(`/loans/${loanId}/approval-status`);
      return data.data;
    },
    enabled: isOpen && !!loanId,
  });

  // ─── Submit ───
  const mutation = useMutation({
    mutationFn: async (payload: { status: string; notes?: string }) => {
      return api.post(`/loans/${loanId}/approve`, payload);
    },
    onSuccess: (response) => {
      toast.success(response.data?.message || "Loan decision submitted");
      queryClient.invalidateQueries(["loan-approval-status", loanId]);
      queryClient.invalidateQueries({ queryKey: ["loans"], exact: false });
      setIsOpen(false);
      setApprovalAction(null);
      form.reset();
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || "Failed to submit decision");
    },
  });

  const handleSubmit = (values: z.infer<typeof approvalSchema>) => {
    if (!approvalAction) return;
    if (approvalAction === "reject" && (!values.notes || !values.notes.trim())) {
      form.setError("notes", { type: "manual", message: "Rejection reason is required" });
      return;
    }
    mutation.mutate({
      status: approvalAction === "approve" ? "Approved" : "Rejected",
      notes: values.notes || undefined,
    });
  };

  // ─── Keyboard Shortcuts ───
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") setIsOpen(false);
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [isOpen, setIsOpen]);

  // ─── System Admin Detection ───
  // Cooperative leaders (President/Secretary/Accountant) are NOT system admins.
  // Only these roles are system admin: admin, Admin, system_admin, System Admin, super_admin, Super Admin, Technical Support
  const isSystemAdmin = useCallback(() => {
    if (!user) return false;
    const role = user.leadershipRole || user.role?.name || user.role || '';
    const normalizedRole = String(role).toLowerCase().trim();
    return ['admin', 'system admin', 'system_admin', 'super admin', 'super_admin', 'technical support'].includes(normalizedRole);
  }, [user]);

  // ─── Role Detection ───
  const getUserRoleName = useCallback(() => {
    if (!user) return null;
    if (isSystemAdmin()) return "Admin";
    return user.leadershipRole || user.role?.name || user.role;
  }, [user, isSystemAdmin]);

  const canUserApprove = useCallback(() => {
    if (!user) return false;
    if (isSystemAdmin()) return true;
    return ["President", "Accountant", "Secretary"].includes(getUserRoleName() || "");
  }, [user, isSystemAdmin, getUserRoleName]);

  const currentUserRole = getUserRoleName();
  const approvalCount = data
    ? [data.presidentApproved, data.secretaryApproved, data.accountantApproved].filter(Boolean).length
    : 0;
  const isFullyApproved = approvalCount >= 3;
  const progressPercent = (approvalCount / 3) * 100;

  const userAlreadyApproved = useMemo(() => {
    if (!user || !data) return false;
    if (isSystemAdmin()) return false; // system admins can always re-approve
    const role = currentUserRole;
    if (role === "President" && data.presidentApproved) return true;
    if (role === "Secretary" && data.secretaryApproved) return true;
    if (role === "Accountant" && data.accountantApproved) return true;
    return false;
  }, [user, data, currentUserRole, isSystemAdmin]);

  // ─── Responsive: Use Sheet on mobile, Dialog on desktop ───
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768);
    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, []);

  const loan = data?.loan;
  const member = data?.memberProfile;
  const savings = data?.savings;
  const risk = data?.risk;
  const schedule = data?.schedule || [];
  const previousLoans = data?.previousLoans || [];
  const prevStats = data?.previousLoanStats || {};
  const groupLeaders = data?.groupLeaders;

  const loanAmount = Number(loan?.amount) || 0;
  const interestRate = Number(loan?.interestRate) || 0;
  const termMonths = Number(loan?.termMonths) || 1;
  const totalInterest = Number(loan?.totalInterest) || Math.round(loanAmount * interestRate / 100);
  const totalRepayment = Number(loan?.totalRepayment) || loanAmount + totalInterest;

  const loanStatusLabel = (s: string) => {
    const map: Record<string, string> = {
      pending_approval: "Pending Approval", approved: "Approved", rejected: "Rejected",
      disbursed: "Disbursed", active: "Active", completed: "Completed",
      overdue: "Overdue", draft: "Draft",
    };
    return map[s] || s?.replace(/_/g, " ") || "Unknown";
  };

  const showDecisionPanel = canUserApprove() && !userAlreadyApproved && (!isFullyApproved || isSystemAdmin());

  // ═══════════════════════════════════════════════════════════════════════════
  // LOADING STATE
  // ═══════════════════════════════════════════════════════════════════════════
  if (isLoading && isOpen) {
    return (
      <Sheet open={isOpen} onOpenChange={setIsOpen}>
        <SheetContent className="w-full max-w-[1200px] p-0 sm:max-w-[1200px]">
          <ModalSkeleton />
        </SheetContent>
      </Sheet>
    );
  }

  if (error && isOpen) {
    return (
      <Sheet open={isOpen} onOpenChange={setIsOpen}>
        <SheetContent className="w-full max-w-[1200px] p-0 sm:max-w-[1200px]">
          <div className="flex flex-col items-center justify-center min-h-[60vh] p-8">
            <div className="h-16 w-16 rounded-full bg-red-100 flex items-center justify-center mb-4">
              <XCircle className="h-8 w-8 text-red-500" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Failed to Load Loan Data</h3>
            <p className="text-sm text-gray-500 text-center max-w-sm mb-4">
              Unable to fetch loan approval details. Please check your connection and try again.
            </p>
            <Button variant="outline" onClick={() => setIsOpen(false)}>Close</Button>
          </div>
        </SheetContent>
      </Sheet>
    );
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // MAIN CONTENT
  // ═══════════════════════════════════════════════════════════════════════════
  return (
    <Sheet open={isOpen} onOpenChange={setIsOpen}>
      <SheetContent className="w-full max-w-[1200px] p-0 sm:max-w-[1200px] overflow-hidden flex flex-col">
        {/* ═══════════ STICKY HEADER ═══════════ */}
        <div className="flex-shrink-0 border-b border-gray-200 bg-white px-6 py-4">
          <div className="flex items-start justify-between gap-4">
            {/* Left: Loan Info */}
            <div className="flex items-start gap-4 min-w-0">
              {/* Member Avatar */}
              <div className="hidden sm:flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-[#009A44] to-[#007a35] text-white shrink-0">
                <span className="text-lg font-bold">{getInitials(member?.fullNames)}</span>
              </div>
              <div className="min-w-0">
                <div className="flex items-center gap-2.5 flex-wrap">
                  <span className="font-mono text-sm font-bold text-gray-900">
                    {loan?.loanCode || `LN-${String(loanId).padStart(4, "0")}`}
                  </span>
                  <StatusBadge status={loan?.status || "pending"} size="md" />
                </div>
                <h2 className="text-lg font-bold text-gray-900 mt-1 truncate">
                  {member?.fullNames || "Loading..."}
                </h2>
                <div className="flex items-center gap-3 mt-1 flex-wrap">
                  <span className="text-xs text-gray-500">
                    Member {formatMemberNumber(member?.memberCode)}
                  </span>
                  {loan?.loanType && (
                    <>
                      <span className="text-gray-300">|</span>
                      <span className="text-xs text-gray-500">{loan.loanType}</span>
                    </>
                  )}
                  {loan?.applicationDate && (
                    <>
                      <span className="text-gray-300">|</span>
                      <span className="text-xs text-gray-500">
                        Applied {format(new Date(loan.applicationDate), "MMM dd, yyyy HH:mm")}
                      </span>
                    </>
                  )}
                </div>
              </div>
            </div>

            {/* Right: Progress + Actions */}
            <div className="flex items-center gap-3 shrink-0">
              {/* Approval Progress */}
              <div className="hidden sm:flex flex-col items-end">
                <span className="text-[11px] font-medium text-gray-500 mb-1">Approval</span>
                <div className="flex items-center gap-1.5">
                  {[0, 1, 2].map(i => (
                    <div
                      key={i}
                      className={cn(
                        "h-2.5 w-8 rounded-full transition-all",
                        i < approvalCount ? "bg-[#009A44]" : "bg-gray-200"
                      )}
                    />
                  ))}
                  <span className="text-xs font-bold text-gray-700 ml-1">{approvalCount}/3</span>
                </div>
              </div>

              <Separator orientation="vertical" className="h-10 hidden sm:block" />

              {/* Action Buttons */}
              <div className="flex items-center gap-1.5">
                <Button variant="ghost" size="sm" className="h-9 w-9 p-0" title="Print">
                  <Printer className="h-4 w-4" />
                </Button>
                <Button variant="ghost" size="sm" className="h-9 w-9 p-0" title="Download">
                  <Download className="h-4 w-4" />
                </Button>
                <Button variant="ghost" size="sm" className="h-9 w-9 p-0" title="Close" onClick={() => setIsOpen(false)}>
                  <X className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* ═══════════ SCROLLABLE BODY ═══════════ */}
        <div className="flex-1 overflow-y-auto">
          <div className="flex flex-col lg:flex-row min-h-full">
            {/* ═══════════ LEFT COLUMN (65%) ═══════════ */}
            <div className="flex-[65] p-6 space-y-6 border-r border-gray-100">

              {/* ─── SECTION 1: Loan Summary ─── */}
              <div>
                <SectionHeader icon={<DollarSign className="h-4 w-4" />} title="Loan Summary" />
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  <MetricCard label="Requested Amount" value={formatCurrency(loanAmount)} icon={<DollarSign className="h-3.5 w-3.5" />} color="green" />
                  <MetricCard label="Interest Rate" value={`${interestRate}%`} icon={<TrendingUp className="h-3.5 w-3.5" />} color="amber" />
                  <MetricCard label="Term" value={`${termMonths} months`} icon={<Calendar className="h-3.5 w-3.5" />} color="blue" />
                  <MetricCard label="Monthly Payment" value={formatCurrency(loan?.monthlyPayment || Math.round(totalRepayment / termMonths))} icon={<CreditCard className="h-3.5 w-3.5" />} color="purple" />
                  <MetricCard label="Total Interest" value={formatCurrency(totalInterest)} icon={<BarChart3 className="h-3.5 w-3.5" />} color="amber" />
                  <MetricCard label="Total Repayment" value={formatCurrency(totalRepayment)} icon={<Wallet className="h-3.5 w-3.5" />} color="green" />
                </div>

                {/* Purpose */}
                {loan?.purpose && (
                  <div className="mt-3 rounded-xl border border-gray-100 bg-gray-50/50 p-4">
                    <div className="flex items-center gap-1.5 mb-1.5">
                      <FileText className="h-3.5 w-3.5 text-gray-500" />
                      <span className="text-[11px] font-medium text-gray-500 uppercase tracking-wide">Purpose</span>
                    </div>
                    <p className="text-sm text-gray-700">{loan.purpose}</p>
                  </div>
                )}
              </div>

              <Separator />

              {/* ─── SECTION 6: Approval Timeline ─── */}
              <div>
                <SectionHeader icon={<GitBranch className="h-4 w-4" />} title="Approval Timeline" />
                <div className="space-y-0">
                  {[
                    { role: "President", approved: data?.presidentApproved, by: data?.presidentApprovedBy, at: data?.presidentApprovedAt, comment: data?.presidentComment, icon: <Shield className="h-4 w-4" /> },
                    { role: "Secretary", approved: data?.secretaryApproved, by: data?.secretaryApprovedBy, at: data?.secretaryApprovedAt, comment: data?.secretaryComment, icon: <User className="h-4 w-4" /> },
                    { role: "Accountant", approved: data?.accountantApproved, by: data?.accountantApprovedBy, at: data?.accountantApprovedAt, comment: data?.accountantComment, icon: <DollarSign className="h-4 w-4" /> },
                  ].map((step, idx) => (
                    <div key={step.role} className="flex gap-4">
                      {/* Timeline line */}
                      <div className="flex flex-col items-center">
                        <div className={cn(
                          "h-10 w-10 rounded-full flex items-center justify-center shrink-0 border-2 transition-all",
                          step.approved
                            ? "bg-[#009A44] border-[#009A44] text-white"
                            : idx < approvalCount
                            ? "bg-gray-100 border-gray-200 text-gray-400"
                            : "bg-white border-gray-300 text-gray-400"
                        )}>
                          {step.approved ? <Check className="h-5 w-5" /> : step.icon}
                        </div>
                        {idx < 2 && (
                          <div className={cn(
                            "w-0.5 h-12 my-1",
                            step.approved ? "bg-[#009A44]" : "bg-gray-200"
                          )} />
                        )}
                      </div>
                      {/* Content */}
                      <div className="pb-6 pt-2 min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-semibold text-gray-900">{step.role}</span>
                          {step.approved ? (
                            <span className="inline-flex items-center rounded-full bg-emerald-100 text-emerald-700 px-2 py-0.5 text-[10px] font-bold">
                              <CheckCircle className="h-3 w-3 mr-0.5" /> Approved
                            </span>
                          ) : (
                            <span className="inline-flex items-center rounded-full bg-amber-100 text-amber-700 px-2 py-0.5 text-[10px] font-bold">
                              <Clock className="h-3 w-3 mr-0.5" /> Pending
                            </span>
                          )}
                        </div>
                        {step.approved && step.by && (
                          <p className="text-xs text-gray-500 mt-1">
                            by {step.by}
                            {step.at && <span className="ml-1">{format(new Date(step.at), "MMM dd, yyyy 'at' HH:mm")}</span>}
                          </p>
                        )}
                        {step.approved && step.comment && (
                          <div className="mt-2 rounded-lg bg-gray-50 border border-gray-100 px-3 py-2 text-xs text-gray-600 italic">
                            "{step.comment}"
                          </div>
                        )}
                        {!step.approved && (
                          <p className="text-xs text-gray-400 mt-1">Awaiting decision</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <Separator />

              {/* ─── SECTION 7: Comments / Decision ─── */}
              <div>
                <SectionHeader icon={<MessageSquare className="h-4 w-4" />} title="Decision" />

                {/* Role Detection */}
                {canUserApprove() && (
                  <div className="rounded-xl bg-blue-50 border border-blue-200 p-3 mb-4">
                    <div className="flex items-center gap-2">
                      <Stamp className="h-4 w-4 text-blue-600" />
                      <span className="text-sm text-blue-800">
                        Approving as: <strong>{currentUserRole}</strong>
                      </span>
                    </div>
                  </div>
                )}

                {/* Access Restricted */}
                {!canUserApprove() && (
                  <div className="rounded-xl bg-amber-50 border border-amber-200 p-3 mb-4">
                    <div className="flex items-start gap-2">
                      <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" />
                      <div className="text-sm text-amber-800">
                        <p className="font-medium">Read-Only Access</p>
                        <p className="text-xs mt-0.5">Only group leaders (President, Secretary, Accountant) can approve loans.</p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Already Approved */}
                {userAlreadyApproved && (
                  <div className="rounded-xl bg-emerald-50 border border-emerald-200 p-3 mb-4">
                    <div className="flex items-start gap-2">
                      <CheckCircle className="h-4 w-4 text-emerald-600 mt-0.5 shrink-0" />
                      <div className="text-sm text-emerald-800">
                        <p className="font-medium">You Already Approved This Loan</p>
                        <p className="text-xs mt-0.5">You have already submitted your approval as {currentUserRole}.</p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Fully Approved */}
                {isFullyApproved && !isSystemAdmin() && (
                  <div className="rounded-xl bg-emerald-50 border-2 border-emerald-300 p-4 text-center mb-4">
                    <CheckCircle className="h-10 w-10 text-[#009A44] mx-auto mb-2" />
                    <p className="text-lg font-bold text-emerald-800">Fully Approved</p>
                    <p className="text-sm text-emerald-600 mt-1">
                      Approved by all 3 leaders. Ready for disbursement.
                    </p>
                  </div>
                )}

                {/* Decision Panel */}
                {showDecisionPanel && (
                  <div className="space-y-4">
                    <div className="flex gap-3">
                      {[
                        { key: "approve" as const, label: "Approve", icon: <CheckCircle className="h-4 w-4" />, color: "bg-[#009A44] hover:bg-[#007a35]" },
                        { key: "reject" as const, label: "Reject", icon: <XCircle className="h-4 w-4" />, color: "bg-red-600 hover:bg-red-700" },
                      ].map(action => (
                        <Button
                          key={action.key}
                          variant={approvalAction === action.key ? "default" : "outline"}
                          onClick={() => setApprovalAction(action.key)}
                          className={cn(
                            "flex-1 h-11 font-semibold",
                            approvalAction === action.key && action.color
                          )}
                        >
                          {action.icon}
                          <span className="ml-2">{action.label}</span>
                        </Button>
                      ))}
                    </div>

                    {approvalAction && (
                      <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-3">
                        <div>
                          <label className="text-xs font-medium text-gray-700 mb-1.5 block">
                            {approvalAction === "reject" ? "Reason for Rejection *" : "Comments (Optional)"}
                          </label>
                          <Textarea
                            placeholder={
                              approvalAction === "reject"
                                ? "Provide a reason for rejecting this loan..."
                                : "Add comments about your decision..."
                            }
                            className="resize-none border-gray-200 min-h-[80px]"
                            rows={3}
                            {...form.register("notes")}
                          />
                          {form.formState.errors.notes && (
                            <p className="text-xs text-red-500 mt-1">{form.formState.errors.notes.message}</p>
                          )}
                        </div>

                        <div className="flex gap-3">
                          <Button
                            type="submit"
                            disabled={mutation.isLoading}
                            className={cn(
                              "flex-1 h-11 font-semibold",
                              approvalAction === "approve"
                                ? "bg-[#009A44] hover:bg-[#007a35]"
                                : "bg-red-600 hover:bg-red-700"
                            )}
                          >
                            {mutation.isLoading ? (
                              <><Loader className="h-4 w-4 mr-2 animate-spin" /> Submitting...</>
                            ) : (
                              <>
                                {approvalAction === "approve"
                                  ? <><CheckCircle className="h-4 w-4 mr-2" /> Confirm Approval</>
                                  : <><XCircle className="h-4 w-4 mr-2" /> Confirm Rejection</>
                                }
                              </>
                            )}
                          </Button>
                          <Button
                            type="button"
                            variant="outline"
                            onClick={() => { setApprovalAction(null); form.reset(); }}
                          >
                            Cancel
                          </Button>
                        </div>
                      </form>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* ═══════════ RIGHT COLUMN (35%) ═══════════ */}
            <div className="flex-[35] bg-gray-50/50 p-6 space-y-6">

              {/* ─── SECTION 2: Member Profile ─── */}
              <div>
                <SectionHeader icon={<User className="h-4 w-4" />} title="Member Profile" />
                <Card className="border-gray-200">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="h-12 w-12 rounded-full bg-gradient-to-br from-[#009A44] to-[#007a35] flex items-center justify-center text-white shrink-0">
                        <span className="text-sm font-bold">{getInitials(member?.fullNames)}</span>
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-bold text-gray-900 truncate">{member?.fullNames || "N/A"}</p>
                        <p className="text-xs text-gray-500">{formatMemberNumber(member?.memberCode)}</p>
                      </div>
                    </div>
                    <div className="space-y-2.5 text-sm">
                      {[
                        { icon: <Phone className="h-3.5 w-3.5" />, label: "Phone", value: member?.phone },
                        { icon: <Mail className="h-3.5 w-3.5" />, label: "Email", value: member?.email },
                        { icon: <Hash className="h-3.5 w-3.5" />, label: "National ID", value: member?.idNumber },
                        { icon: <Calendar className="h-3.5 w-3.5" />, label: "Joined", value: member?.joinedAt ? format(new Date(member.joinedAt), "MMM yyyy") : null },
                        { icon: <Briefcase className="h-3.5 w-3.5" />, label: "Employment", value: member?.employmentType },
                        { icon: <Building2 className="h-3.5 w-3.5" />, label: "Employer", value: member?.employerName },
                      ].filter(f => f.value).map(field => (
                        <div key={field.label} className="flex items-center gap-2.5">
                          <span className="text-gray-400">{field.icon}</span>
                          <span className="text-gray-500 w-20 shrink-0">{field.label}</span>
                          <span className="font-medium text-gray-900 truncate">{field.value}</span>
                        </div>
                      ))}
                    </div>
                    <div className="mt-3 pt-3 border-t border-gray-100 flex gap-2">
                      <Button variant="outline" size="sm" className="flex-1 text-xs h-8">
                        <User className="h-3 w-3 mr-1" /> Profile
                      </Button>
                      <Button variant="outline" size="sm" className="flex-1 text-xs h-8">
                        <Phone className="h-3 w-3 mr-1" /> Call
                      </Button>
                      <Button variant="outline" size="sm" className="flex-1 text-xs h-8">
                        <Send className="h-3 w-3 mr-1" /> Notify
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* ─── SECTION 3: Savings Analysis ─── */}
              <div>
                <SectionHeader icon={<Wallet className="h-4 w-4" />} title="Savings Analysis" />
                <div className="grid grid-cols-2 gap-2.5">
                  <MetricCard label="Current Savings" value={formatCurrency(savings?.totalSavings || 0)} icon={<Wallet className="h-3.5 w-3.5" />} color="green" />
                  <MetricCard label="Avg Monthly" value={formatCurrency(savings?.avgMonthlySavings || 0)} icon={<BarChart3 className="h-3.5 w-3.5" />} color="blue" />
                  <MetricCard label="Contributions" value={savings?.contributionCount || 0} icon={<History className="h-3.5 w-3.5" />} color="purple" />
                  <MetricCard label="Consistency" value={`${savings?.contributionConsistency || 0}%`} icon={<Target className="h-3.5 w-3.5" />} color="amber" />
                </div>

                {/* Eligibility Indicator */}
                <div className={cn(
                  "mt-3 rounded-xl border-2 p-3 flex items-center gap-3",
                  risk?.eligibilityStatus === "eligible"
                    ? "bg-emerald-50 border-emerald-200"
                    : "bg-amber-50 border-amber-200"
                )}>
                  <div className={cn(
                    "h-10 w-10 rounded-full flex items-center justify-center shrink-0",
                    risk?.eligibilityStatus === "eligible" ? "bg-emerald-100" : "bg-amber-100"
                  )}>
                    {risk?.eligibilityStatus === "eligible" ? (
                      <CheckCircle className="h-5 w-5 text-emerald-600" />
                    ) : (
                      <AlertTriangle className="h-5 w-5 text-amber-600" />
                    )}
                  </div>
                  <div>
                    <p className={cn(
                      "text-sm font-bold",
                      risk?.eligibilityStatus === "eligible" ? "text-emerald-700" : "text-amber-700"
                    )}>
                      {risk?.eligibilityStatus === "eligible" ? "Eligible for Loan" : "Needs Review"}
                    </p>
                    <p className="text-xs text-gray-600">
                      Max: {formatCurrency(risk?.maxLoanAmount || 0)} | Requested: {formatCurrency(loanAmount)}
                    </p>
                  </div>
                </div>
              </div>

              {/* ─── SECTION 5: Risk Assessment ─── */}
              <div>
                <SectionHeader icon={<Scale className="h-4 w-4" />} title="Risk Assessment" />
                <div className="space-y-2.5">
                  <div className="flex items-center justify-between rounded-xl bg-white border border-gray-100 px-4 py-3">
                    <span className="text-xs font-medium text-gray-600">Overall Risk</span>
                    <RiskBadge level={risk?.level || "LOW"} />
                  </div>
                  {[
                    { label: "Repayment Capacity", value: `${risk?.repaymentCapacity || 0}%`, color: (risk?.repaymentCapacity || 0) >= 50 ? "text-emerald-600" : (risk?.repaymentCapacity || 0) >= 25 ? "text-amber-600" : "text-red-600" },
                    { label: "Debt Ratio", value: `${risk?.debtRatio || 0}%`, color: (risk?.debtRatio || 0) <= 30 ? "text-emerald-600" : (risk?.debtRatio || 0) <= 60 ? "text-amber-600" : "text-red-600" },
                    { label: "Savings Ratio", value: `${risk?.savingsRatio || 0}%`, color: (risk?.savingsRatio || 0) >= 50 ? "text-emerald-600" : (risk?.savingsRatio || 0) >= 25 ? "text-amber-600" : "text-red-600" },
                    { label: "Late Payments", value: String(risk?.pendingPenalties ? 1 : 0), color: risk?.pendingPenalties ? "text-red-600" : "text-emerald-600" },
                    { label: "Defaulted Loans", value: String(prevStats?.defaults || 0), color: (prevStats?.defaults || 0) > 0 ? "text-red-600" : "text-emerald-600" },
                  ].map(metric => (
                    <div key={metric.label} className="flex items-center justify-between rounded-lg bg-white border border-gray-100 px-4 py-2.5">
                      <span className="text-xs text-gray-600">{metric.label}</span>
                      <span className={cn("text-sm font-bold", metric.color)}>{metric.value}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* ─── SECTION 4: Loan History ─── */}
              <div>
                <SectionHeader icon={<History className="h-4 w-4" />} title="Previous Loans ({previousLoans.length})" />
                {previousLoans.length === 0 ? (
                  <div className="rounded-xl bg-white border border-gray-100 p-6 text-center">
                    <History className="h-8 w-8 text-gray-300 mx-auto mb-2" />
                    <p className="text-xs text-gray-400">No previous loans</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {previousLoans.slice(0, 5).map((prev: any) => (
                      <div key={prev.id} className="rounded-lg bg-white border border-gray-100 p-3">
                        <div className="flex items-center justify-between mb-1.5">
                          <span className="text-xs font-mono font-bold text-gray-700">{prev.loanCode}</span>
                          <StatusBadge status={prev.status} size="sm" />
                        </div>
                        <div className="flex items-center justify-between text-xs">
                          <span className="text-gray-600">{formatCurrency(prev.amount)}</span>
                          <span className="text-gray-500">Paid: {formatCurrency(prev.totalPaid)}</span>
                        </div>
                        <div className="mt-1.5">
                          <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                            <div
                              className="h-full bg-[#009A44] rounded-full"
                              style={{ width: `${Math.min(100, prev.amount > 0 ? (prev.totalPaid / prev.amount) * 100 : 0)}%` }}
                            />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* ═══════════ STICKY FOOTER ═══════════ */}
        <div className="flex-shrink-0 border-t border-gray-200 bg-white px-6 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Button variant="outline" onClick={() => setIsOpen(false)} className="h-9">
                Cancel
              </Button>
              <Button variant="ghost" className="h-9 text-gray-500">
                Save Draft
              </Button>
            </div>
            <div className="flex items-center gap-2">
              {showDecisionPanel && (
                <>
                  <Button
                    variant="outline"
                    className="h-9 border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700"
                    onClick={() => setApprovalAction("reject")}
                  >
                    <XCircle className="h-4 w-4 mr-1.5" />
                    Reject
                  </Button>
                  <Button
                    className="h-9 bg-[#009A44] hover:bg-[#007a35]"
                    onClick={() => {
                      setApprovalAction("approve");
                      form.handleSubmit(handleSubmit)();
                    }}
                    disabled={mutation.isLoading}
                  >
                    {mutation.isLoading ? (
                      <Loader className="h-4 w-4 mr-1.5 animate-spin" />
                    ) : (
                      <CheckCircle className="h-4 w-4 mr-1.5" />
                    )}
                    Approve
                  </Button>
                </>
              )}
              {isFullyApproved && !showDecisionPanel && (
                <Badge className="bg-[#009A44] text-white px-3 py-1">
                  <CheckCircle className="h-3.5 w-3.5 mr-1" /> Fully Approved
                </Badge>
              )}
            </div>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
